/** XeAgents.ai**/

/**
 * Which price list a visitor is quoted.
 *
 * The plans table only carries 'IN' and 'US' rows and /api/plans normalises
 * anything that is not India to 'US', so those two are the only price lists
 * that exist: India pays in INR, everyone else pays in USD.
 */
export type PricingRegion = "IN" | "US";

export type PricingCurrency = "INR" | "USD";

export const DEFAULT_PRICING_REGION: PricingRegion = "US";

export const PRICING_REGION_CURRENCY: Record<PricingRegion, PricingCurrency> = {
  IN: "INR",
  US: "USD",
};

export const PRICING_CURRENCY_SYMBOL: Record<PricingCurrency, string> = {
  INR: "₹",
  USD: "$",
};

/**
 * India keeps a single offset, so these two IANA names (Calcutta is the old
 * alias some browsers still report) are the whole of it. Any other real zone
 * therefore places the visitor outside India.
 */
const INDIA_TIME_ZONES = new Set(["asia/kolkata", "asia/calcutta"]);

/**
 * Languages whose speakers are overwhelmingly in India, used only when the
 * browser gives a language with no region attached ("hi" rather than "hi-IN").
 * Deliberately excludes languages with large populations elsewhere - Bengali
 * (Bangladesh), Tamil (Sri Lanka, Singapore), Urdu (Pakistan).
 */
const INDIA_ONLY_LANGUAGES = new Set([
  "hi",
  "mr",
  "gu",
  "kn",
  "ml",
  "te",
  "pa",
  "or",
  "as",
]);

/** Codes CDNs send when they could not place the IP at all. */
const UNKNOWN_COUNTRY_CODES = new Set(["XX", "T1", "O1", "AP", "EU"]);

/**
 * Maps an ISO 3166-1 country - from a CDN header or a stored billing country -
 * onto a price list. Returns null when the value tells us nothing, so callers
 * can fall through to a weaker signal instead of defaulting to USD too early.
 */
export function regionFromCountry(country?: string | null): PricingRegion | null {
  if (!country) return null;
  const code = country.trim().toUpperCase();
  if (code === "IN" || code === "IND" || code === "INDIA") return "IN";
  // Stored billing countries are not always ISO codes - signup and the Upgrade
  // page both accept the spelled-out forms.
  if (code === "US" || code === "USA") return "US";
  if (UNKNOWN_COUNTRY_CODES.has(code)) return null;
  if (!/^[A-Z]{2}$/.test(code)) return null;
  return "US";
}

/**
 * Reads the browser time zone. Deliberately broader than countryFromTimezone
 * in shared/countries.ts, which only knows the ten countries signup offers:
 * pricing is a two-way split, so every zone that is not India is USD rather
 * than unknown. "UTC", "GMT" and the Etc/* zones are machine defaults rather
 * than places, so they resolve to null and let a weaker signal answer.
 */
export function regionFromTimeZone(timeZone?: string | null): PricingRegion | null {
  if (!timeZone) return null;
  const zone = timeZone.trim().toLowerCase();
  if (!zone.includes("/") || zone.startsWith("etc/")) return null;
  return INDIA_TIME_ZONES.has(zone) ? "IN" : "US";
}

/** Pulls the region subtag out of a BCP 47 tag: "en-IN" -> "IN", "en" -> null. */
function regionSubtag(locale: string): string | null {
  const parts = locale.trim().split(/[-_]/);
  for (let i = 1; i < parts.length; i++) {
    const part = parts[i];
    if (part.length === 2 && /^[A-Za-z]{2}$/.test(part)) {
      return part.toUpperCase();
    }
  }
  return null;
}

/**
 * Walks navigator.languages in preference order and stops at the first tag
 * that names a place, either through its region subtag or by being an
 * India-only language.
 */
export function regionFromLocales(locales?: readonly string[] | null): PricingRegion | null {
  if (!locales) return null;
  for (const locale of locales) {
    if (!locale) continue;
    const region = regionSubtag(locale);
    if (region) return region === "IN" ? "IN" : "US";
    if (INDIA_ONLY_LANGUAGES.has(locale.trim().toLowerCase())) return "IN";
  }
  return null;
}

export interface PricingRegionSignals {
  /** ISO country from a CDN/proxy header - the only signal a VPN cannot fake. */
  country?: string | null;
  /** Intl.DateTimeFormat().resolvedOptions().timeZone */
  timeZone?: string | null;
  /** navigator.languages */
  locales?: readonly string[] | null;
}

/**
 * Picks the price list for a visitor, strongest signal first: an IP-derived
 * country header, then the browser's time zone, then its languages. Falls back
 * to USD, which is the international list.
 */
export function detectPricingRegion(signals: PricingRegionSignals): PricingRegion {
  return (
    regionFromCountry(signals.country) ??
    regionFromTimeZone(signals.timeZone) ??
    regionFromLocales(signals.locales) ??
    DEFAULT_PRICING_REGION
  );
}

/**
 * Formats a plan price in its own currency, using Indian digit grouping for
 * INR (2,99,990 rather than 299,990) and dropping the decimals on the whole
 * amounts every plan currently uses.
 */
export function formatPlanPrice(amount: number, currency: PricingCurrency): string {
  const locale = currency === "INR" ? "en-IN" : "en-US";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    }).format(amount);
  } catch {
    return `${PRICING_CURRENCY_SYMBOL[currency]}${amount.toLocaleString(locale)}`;
  }
}

/**
 * Discount a yearly price represents against twelve monthly payments, rounded
 * to a whole percent. Returns 0 when there is nothing to save, so the landing
 * page can quote the real number instead of a hardcoded "save 20%".
 */
export function yearlySavingsPercent(monthlyPrice: number, yearlyPrice: number | null): number {
  if (!monthlyPrice || !yearlyPrice || yearlyPrice <= 0) return 0;
  const fullYear = monthlyPrice * 12;
  if (yearlyPrice >= fullYear) return 0;
  return Math.round(((fullYear - yearlyPrice) / fullYear) * 100);
}
